/** The records screen: offices and tabs, index books, book and page, estate records, the viewer, page details,
 *  copies and printing, and the staff tools. Everything comes from the API; nothing is kept in the browser. */
import type { ActivityEntry, BookPageResponse, EstateRecordResponse, EstateRow, EstateSearchResponse, IndexBookInfo, IndexKeyInfo, IndexLookupResponse, IndexMethod,
  IndexVolumeResponse, Me, OfficeInfo, PageRef, PrintJob, Redaction } from '@imagesync/core';
import { DOCKET_HINT, DOCKET_PATTERN, methodInfo } from '@imagesync/core';
import { ApiError, del, fetchFile, get, patch, post, put, setCsrf } from './api.js';
import { can, canAdmin, cur, curPage, displayName, form, hasModule, isGuest, me, office, officeSlug, perms, role, S, type EstateQuery, type IndexEntry, type Seq, type SeqItem, type Tab } from './state.js';
import { $, $$, $maybe, ARROW, DEG, DOT, EN, esc, fmtDate, money, NOKEY, num, pad4, plural, saveBlob, setErr, spaced, throttle, timeLabel, toast } from './util.js';
import { applyFilters, boxes, closeViewer, currentRegion, fit, hasImage, initViewer, openPage, rotateBy, rotation, setRotation, zoomBy } from './viewer.js';
import { closeAdmin, isAdminOpen, openAdmin, wireAdmin } from './admin.js';
import { openImport, wireImport } from './imports.js';
import { helpContext, isHelpOpen, openHelp, closeHelp, refreshHelp, wireHelp } from './help.js';

const MODULE_LABELS: Record<Tab, string> = { index: 'Index books', bookpage: 'Book & page', estates: 'Estates' };

/* ------------------------------------------------------------ small helpers */
function stageMessage(text: string): void { $('#stageMsgText').textContent = text; $('#stageMsg').hidden = false; }
function hideStageMessage(): void { $('#stageMsg').hidden = true; }
/** The lookup steps come back with values in `backticks`; show them as code. */
const stepHTML = (s: string) => esc(s).replace(/`([^`]*)`/g, '<code>$1</code>');
const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.');
const indexById = (id: string): IndexBookInfo | undefined => office().indexes.find(i => i.id === id);
const bookTypeLabel = (id: string) => office().bookTypes.find(b => b.id === id);
const seriesLabel = (ix: IndexBookInfo, code: string) => ix.series.find(s => s.id === code)?.label ?? code;
const usePublic = (p: PageRef | null) => !!p && p.variant === 'original' && S.viewAsPublic && p.redactions > 0;

/* ------------------------------------------------------------ sequences: what the thumbnails and prev/next walk through */
function bookSeq(r: BookPageResponse): Seq {
  return { type: 'book', book: r.book, document: r.document, prevPage: r.prevPage, nextPage: r.nextPage, firstPage: r.firstPage, lastPage: r.lastPage,
    items: r.pages.map(p => ({ page: p, label: p.label, doc: r.document })) };
}
function indexSeq(ix: IndexBookInfo, v: IndexVolumeResponse): Seq {
  const items: SeqItem[] = [];
  if (v.guide) items.push({ page: v.guide, label: 'Guide', section: null });
  for (const s of v.sections) if (s.page) items.push({ page: s.page, label: pad4(s.section), section: s });
  return { type: 'index', index: ix, series: v.series, seriesLabel: seriesLabel(ix, v.series), volumeBook: v.book, items };
}
function estateSeq(rec: EstateRecordResponse): Seq {
  return { type: 'estate', record: rec, items: rec.pagesList.map((p, i) => ({ page: p, label: String(i + 1), n: i + 1 })) };
}
/** One row of a book's lookup table, for the screen: a Cott or Graves beginning ("ABR", "ADAM only", "AB + CDE" for
 *  the letters that may follow), or a key and its given-name column ("L N · A–E"). */
function keyText(k: IndexKeyInfo, method: IndexMethod): string {
  const beginnings = method === 'cott' || method === 'graves';
  if (beginnings) return k.exact ? k.key + ' only' : !k.key ? 'any name' : k.col ? k.key + ' + ' + k.col : k.key;
  return (!k.key ? 'no key' : spaced(k.key)) + (k.col ? ' ' + DOT + ' ' + k.col : '');
}
/** What a section holds, from its rows: a few rows spelled out, many as a range (the API sends the first three and
 *  the last, with the count). */
function sectionTag(s: { keys: IndexKeyInfo[]; keyCount: number }, method: IndexMethod, long = false): string {
  const ks = s.keys;
  if (!ks.length) return long ? 'not in the book’s lookup table' : '';
  const beginnings = method === 'cott' || method === 'graves';
  const texts = ks.map(k => keyText(k, method));
  const list = s.keyCount <= 3 ? texts.join(', ') : texts[0] + ' … ' + texts[texts.length - 1] + ' (' + s.keyCount + ')';
  if (!long) return list;
  return beginnings ? (ks.length === 1 && ks[0]!.exact ? 'the name ' + list : 'names beginning ' + list) : 'key ' + list;
}
function pageName(it: SeqItem | null = cur()): string {
  const seq = S.seq;
  if (!seq || !it) return '';
  if (seq.type === 'book') return seq.book!.label + ' ' + seq.book!.number + ', page ' + it.label;
  if (seq.type === 'index') return seq.index!.label + ', Book ' + seq.volumeBook + ', ' + (it.section ? 'section ' + it.label : 'section guide');
  return 'Docket ' + seq.record!.docket + ', page ' + it.n;
}

/* ------------------------------------------------------------ opening pages */
let lastOpened: string | null = null;
async function openIdx(i: number): Promise<void> {
  const seq = S.seq;
  if (!seq || !seq.items.length) return;
  // stepping past either end of a document moves to the neighbouring document in the book
  if (seq.type === 'book' && (i < 0 || i >= seq.items.length)) {
    const label = i < 0 ? seq.prevPage : seq.nextPage;
    if (label) { await runBookPage(seq.book!.type, seq.book!.number, label, { quiet: true, atEnd: i < 0 }); }
    return;
  }
  S.idx = Math.max(0, Math.min(seq.items.length - 1, i));
  const it = cur()!;
  renderThumbsActive(); renderPageLabel(); renderDetails(); resetPrintRange(); updateBadges(); updateStaffUI(); renderIdxChips();
  hideStageMessage();
  lastOpened = it.page.id;
  try {
    await openPage(it.page, usePublic(it.page));
  } catch (e) {
    if (lastOpened === it.page.id) stageMessage(errMsg(e));
  }
}

async function onOpened(): Promise<void> {
  const it = cur();
  if (!it) return;
  setRotation(it.page.rotation || 0);
  fit('page', true);
  applyFilters(S.gray, S.invert);
  await loadBoxes();
  updateBadges(); updateStaffUI(); updateIiifLine();
}

/* ------------------------------------------------------------ boxes: redactions (staff) and highlights (anyone) */
let savedBoxes: Redaction[] = [];
async function loadBoxes(): Promise<void> {
  const b = boxes, it = cur();
  if (!b || !it) return;
  savedBoxes = [];
  if (can('originals') && !S.viewAsPublic && it.page.redactions > 0) {
    try {
      const pg = await get<{ boxes: Redaction[] }>('/api/v1/pages/' + it.page.id);
      if (cur()?.page.id !== it.page.id) return;
      savedBoxes = pg.boxes;
    } catch { /* the page shows without its boxes; Apply stays off */ }
  }
  b.set(savedBoxes.map(r => ({ ...r, kind: 'redaction' as const })));
  setTool(S.tool, true);
  updateClearBtn();
}
const rectSig = (list: { x: number; y: number; w: number; h: number }[]) => JSON.stringify(list.map(r => [r.x, r.y, r.w, r.h]).sort());
function isDirty(): boolean {
  if (!curPage() || !can('originals') || S.viewAsPublic || !boxes) return false;
  return rectSig(boxes.list('redaction')) !== rectSig(savedBoxes);
}
function setTool(t: typeof S.tool, silent = false): void {
  const p = curPage();
  if (t === 'redact' && (!can('originals') || S.viewAsPublic || !p)) t = 'pan';
  S.tool = t;
  $$('.tools [data-tool]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.tool === t)));
  boxes?.setMode(t === 'redact' ? 'redaction' : t === 'highlight' ? 'highlight' : null);
  if (!silent && !S.hinted[t]) {
    S.hinted[t] = true;
    if (t === 'redact') toast('Drag on the page to draw a redaction box. Then choose Apply to public copy.');
    if (t === 'highlight') toast('Drag on the page to highlight. Highlights clear when you change pages.');
  }
  updateStaffUI();
}
async function applyRedactions(): Promise<void> {
  const it = cur();
  if (!it || !boxes) return;
  const rects = boxes.list('redaction').map(r => ({ x: r.x, y: r.y, w: r.w, h: r.h }));
  const btn = $<HTMLButtonElement>('#applyRedact');
  btn.disabled = true;
  try {
    const r = await put<{ boxes: Redaction[]; publicRev: number; publicStatus: string }>(`/api/v1/pages/${it.page.id}/redactions`, { boxes: rects });
    savedBoxes = r.boxes;
    it.page.redactions = r.boxes.length; it.page.publicRev = r.publicRev; it.page.publicReady = r.publicStatus !== 'pending';
    boxes.set([...boxes.list('highlight'), ...r.boxes.map(b => ({ ...b, kind: 'redaction' as const }))]);
    setTool(S.tool, true);
    toast(r.boxes.length ? `Saved. The public copy is being made with ${r.boxes.length} ${plural(r.boxes.length, 'box', 'boxes')} burned in.` : 'Redactions removed. The public now sees the original page.');
    void loadActivity();
    if (r.publicStatus === 'pending') watchPublicCopy(it.page);
  } catch (e) { toast(errMsg(e), 'err'); } finally { updateStaffUI(); updateBadges(); renderDetails(); }
}
/** Poll until the new public copy is ready, so the badge and "view as public" show it. */
function watchPublicCopy(p: PageRef): void {
  let tries = 0;
  const tick = async () => {
    if (++tries > 40) return;
    try {
      const m = await get<PageRef & { publicStatus: string }>('/api/v1/pages/' + p.id);
      if (m.publicStatus === 'ready') { p.publicReady = true; p.publicRev = m.publicRev; if (curPage()?.id === p.id) { updateBadges(); updateStaffUI(); } return; }
    } catch { return; }
    setTimeout(tick, 1500);
  };
  setTimeout(tick, 1200);
}
function discardRedactions(): void {
  if (!boxes) return;
  boxes.set([...boxes.list('highlight'), ...savedBoxes.map(b => ({ ...b, kind: 'redaction' as const }))]);
  setTool(S.tool, true);
  updateStaffUI(); updateBadges(); toast('Changes discarded.');
}
function clearableKinds(): ('redaction' | 'highlight')[] { return can('originals') && !S.viewAsPublic ? ['highlight', 'redaction'] : ['highlight']; }
function updateClearBtn(): void {
  const n = boxes ? boxes.list().filter(b => clearableKinds().includes(b.kind)).length : 0, b = $<HTMLButtonElement>('#clearHl');
  b.disabled = !n;
  b.title = n ? (clearableKinds().length > 1 ? 'Remove the highlights and redaction boxes on this page' : 'Remove the highlights on this page') : 'Nothing to clear on this page';
}
function clearMarks(): void {
  if (!boxes) return;
  const hadBoxes = boxes.list('redaction').length > 0 && clearableKinds().includes('redaction');
  const n = boxes.remove(clearableKinds());
  if (!n) { toast('Nothing to clear on this page.'); return; }
  afterBoxesChanged();
  toast(hadBoxes ? 'Cleared. Choose Apply to public copy to remove the redactions for everyone, or Discard changes to bring them back.' : 'Highlights cleared.');
}
function afterBoxesChanged(): void { updateStaffUI(); updateBadges(); updateClearBtn(); }
async function saveRotation(): Promise<void> {
  const it = cur();
  if (!it || !hasImage()) return;
  const r = rotation();
  try {
    await put(`/api/v1/pages/${it.page.id}/rotation`, { degrees: r });
    it.page.rotation = r;
    toast('Saved. This page now opens at ' + r + DEG + ' for everyone.');
    void loadActivity();
  } catch (e) { toast(errMsg(e), 'err'); }
  updateStaffUI(); updateBadges(); renderDetails();
}

/* ------------------------------------------------------------ the stage: badges, page label, thumbnails */
function updateBadges(): void {
  const el = $('#badges');
  el.innerHTML = '';
  const p = curPage();
  if (!p) return;
  const add = (t: string, cls?: string) => { const d = document.createElement('div'); d.className = 'badge' + (cls ? ' ' + cls : ''); d.textContent = t; el.appendChild(d); };
  const n = p.redactions;
  if (p.variant === 'public' || usePublic(p)) add('Public copy' + (can('originals') ? ' ' + DOT + ' ' + n + ' ' + plural(n, 'redaction') + ' burned in' : ''), 'pub');
  else if (can('originals') && n) add('Original ' + DOT + ' the public sees ' + n + ' ' + plural(n, 'box', 'boxes') + ' blacked out' + (p.publicReady ? '' : ' (being made)'), 'orig');
  if (isDirty()) add('Unapplied redaction changes', 'warn');
  if (p.negative) add(S.invert ? 'Microfilm negative ' + DOT + ' shown inverted' : 'Microfilm negative ' + DOT + ' try Invert');
  if (p.scannedSideways && !rotation()) add('Scanned sideways ' + DOT + ' rotate right to read');
}
function renderPageLabel(): void {
  const el = $('#pageLabel'), it = cur(), seq = S.seq;
  const prev = $<HTMLButtonElement>('#prevBtn'), next = $<HTMLButtonElement>('#nextBtn');
  if (!it || !seq) { el.textContent = NOKEY; prev.disabled = true; next.disabled = true; return; }
  const n = seq.items.length;
  let main = it.label, sub = (S.idx + 1) + ' of ' + n;
  if (seq.type === 'book') { main = 'Page ' + it.label; sub = 'pages ' + seq.firstPage + EN + seq.lastPage; }
  else if (seq.type === 'index') { main = it.section ? 'Section ' + it.label : 'Section guide'; sub = 'Book ' + seq.volumeBook; }
  else { main = 'Page ' + it.n; sub = 'of ' + n; }
  el.innerHTML = esc(main) + '<small>' + esc(sub) + '</small>';
  prev.disabled = S.idx <= 0 && !(seq.type === 'book' && seq.prevPage);
  next.disabled = S.idx >= n - 1 && !(seq.type === 'book' && seq.nextPage);
}
function renderThumbs(): void {
  const el = $('#thumbs');
  el.innerHTML = '';
  const seq = S.seq;
  if (!seq || !seq.items.length) {
    const p = document.createElement('p');
    p.className = 'thumbs-empty';
    p.textContent = S.record && !S.record.pages ? 'No case file is on record for this docket.' : 'Search to open a book, an index section or a record.';
    el.appendChild(p);
    return;
  }
  let label = '';
  if (seq.type === 'book') { const d = seq.document!; label = seq.book!.short + ' ' + seq.book!.number + ' ' + DOT + ' ' + d.first + (d.last !== d.first ? EN + d.last : ''); }
  else if (seq.type === 'index') label = seq.index!.label + ' ' + DOT + ' Book ' + seq.volumeBook + ' ' + DOT + ' ' + seq.seriesLabel;
  else label = 'Docket ' + seq.record!.docket;
  const gd = document.createElement('div');
  gd.className = 'tgroup';
  gd.innerHTML = '<div class="tgroup-label">' + esc(label) + '</div>';
  const row = document.createElement('div');
  row.className = 'trow';
  seq.items.forEach((it, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'thumb'; b.dataset.i = String(i);
    b.setAttribute('aria-label', pageName(it));
    b.innerHTML = '<img src="' + esc(it.page.thumb) + '" alt="" width="56" height="78" loading="lazy"><span>' + esc(it.section === null ? 'Guide' : it.label) + '</span>';
    const img = b.querySelector('img')!;
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
    row.appendChild(b);
  });
  gd.appendChild(row);
  el.appendChild(gd);
  renderThumbsActive();
}
function renderThumbsActive(): void {
  const strip = $('#thumbs');
  $$('#thumbs .thumb').forEach(b => {
    const on = Number(b.dataset.i) === S.idx;
    b.classList.toggle('active', on);
    if (on) {
      b.setAttribute('aria-current', 'page');
      const br = b.getBoundingClientRect(), sr = strip.getBoundingClientRect();
      if (br.left < sr.left || br.right > sr.right) strip.scrollLeft += (br.left - sr.left) - (sr.width - br.width) / 2;
    } else b.removeAttribute('aria-current');
  });
}

/* ------------------------------------------------------------ details */
const factsHTML = (rows: [string, string][]) => rows.map(r => '<dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd>').join('');
function storageRows(p: PageRef): [string, string][] {
  const s = p.storage;
  if (!s) return [];
  const LABEL: Record<string, string> = { 'tif-pages': 'Single-page TIFF, one file per page', 'tif-doc': 'Multi-page TIFF, one file for the document',
    'pdf-pages': 'Single-page PDF, one file per page', 'pdf-doc': 'Multi-page PDF, one file for the document', image: 'Image, one file per page' };
  const size = s.bytes >= 1048576 ? (s.bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(s.bytes / 1024)) + ' KB';
  return [['Stored as', LABEL[s.layout] ?? s.layout], ['File', s.file + (s.filePages > 1 ? ', page ' + s.filePage + ' of ' + s.filePages : '') + ' ' + DOT + ' ' + size],
    ['Encoding', [s.compression, s.color, s.dpi ? s.dpi + ' dpi' : ''].filter(Boolean).join(', ')]];
}
function pageFacts(p: PageRef): [string, string][] {
  const rows: [string, string][] = [['Image', p.width.toLocaleString() + ' × ' + p.height.toLocaleString() + ' px'], ...storageRows(p)];
  rows.push(['Saved rotation', (p.rotation || 0) + DEG + (p.scannedSideways && !p.rotation ? ' (scanned sideways)' : '')]);
  if (can('originals')) rows.push(['Public copy', p.redactions ? p.redactions + ' ' + plural(p.redactions, 'redaction') + ' burned in' + (p.publicReady ? '' : ' (being made)') : 'Same as the original']);
  return rows;
}
function renderDetails(): void {
  const it = cur(), title = $('#docTitle'), sub = $('#docSub'), facts = $('#docFacts'), extra = $('#docExtra');
  extra.innerHTML = '';
  if (S.tab[officeSlug()] === 'estates') { renderRecordDetails(); renderPrintBlock(); return; }
  if (!it || !S.seq) {
    title.textContent = 'Nothing open';
    sub.textContent = hasModule('index') ? 'Search an index, or look up a book and page.' : 'Look up a book and page.';
    facts.innerHTML = '';
    renderPrintBlock();
    return;
  }
  const seq = S.seq, rows: [string, string][] = [];
  if (seq.type === 'book') {
    const b = seq.book!, d = seq.document!, k = S.idx + 1, n = seq.items.length;
    title.textContent = b.label + ' ' + b.number + ' ' + DOT + ' Page ' + it.label;
    const range = 'pages ' + d.first + EN + d.last;
    sub.textContent = n === 1 ? 'A one-page document.' : (k === 1 ? 'First page of a ' + n + '-page document (' + range + ').' : 'Page ' + k + ' of ' + n + ' in the document at ' + range + '.');
    rows.push(['Document', d.kind + (d.recorded ? ', recorded ' + fmtDate(d.recorded) : '')]);
    for (const p of d.parties) rows.push([p.role, p.names]);
    if (d.instrument) rows.push(['Instrument', d.instrument]);
    rows.push(['Collection', b.label.replace(/ Book$/, '') + ' books ' + DOT + ' ' + office().name]);
    if (b.negative) rows.push(['Film', 'Microfilm negative. Invert shows it as a positive.']);
    if (b.restricted) rows.push(['Access', 'Restricted book. Only accounts allowed restricted books can open it.']);
    if (can('deleteRecords')) extra.innerHTML = '<div class="actions"><button type="button" class="btn small danger" id="removeDoc" data-confirm="Remove it: ' + esc(b.label + ' ' + b.number + ', ' + range) + '">Remove this document…</button></div>';
  } else if (seq.type === 'index') {
    const ix = seq.index!;
    if (it.section) {
      title.textContent = ix.label + ' ' + DOT + ' Section ' + it.label;
      sub.textContent = 'Book ' + seq.volumeBook + ' ' + DOT + ' ' + seq.seriesLabel + ' ' + DOT + ' ' + sectionTag(it.section, ix.method, true);
    } else {
      title.textContent = ix.label + ' ' + DOT + ' Section guide';
      sub.textContent = 'Book ' + seq.volumeBook + ' ' + DOT + ' ' + seq.seriesLabel + '. ' + (ix.method === 'cott'
        ? 'Find the longest beginning listed that the surname starts with.' : ix.method === 'graves' ? 'Find the name in the guide.'
        : methodInfo(ix.method).usesInitial ? 'Find the key on the left, then read across to the given-name column.' : 'Find the key on the left.');
    }
    rows.push(['Collection', 'Index books ' + DOT + ' ' + office().name]);
    rows.push(['Lookup method', methodInfo(ix.method).label]);
  }
  facts.innerHTML = factsHTML(rows.concat(pageFacts(it.page)));
  if (seq.type === 'index' && it.section) void renderEntryShortcuts(seq, it);
  renderPrintBlock();
}
/** Index entries typed in with a book and page get an Open button. */
async function renderEntryShortcuts(seq: Seq, it: SeqItem): Promise<void> {
  const ix = seq.index!;
  if (!ix.bookType || !hasModule('bookpage')) return;
  try {
    const r = await get<{ entries: IndexEntry[] }>(`/api/v1/indexes/${ix.id}/volumes/${encodeURIComponent(seq.series!)}/${seq.volumeBook}/sections/${it.section!.section}`);
    if (cur() !== it) return;
    const hits = r.entries.filter(e => e.book && e.page);
    if (!hits.length) return;
    const bl = bookTypeLabel(ix.bookType)?.label ?? 'Book';
    $('#docExtra').innerHTML = '<div class="shortcuts"><span class="label">Entries with a book and page</span>' + hits.map(e =>
      '<div class="shortcut"><span>' + esc(e.surname) + ', ' + esc(e.given) + ' ' + ARROW + ' ' + esc(bl) + ' ' + esc(e.book) + ', page ' + esc(e.page) + '</span>' +
      '<button type="button" class="btn small" data-open-bp="' + esc(ix.bookType + '|' + e.book + '|' + e.page) + '">Open</button></div>').join('') + '</div>';
  } catch { /* no entries */ }
}
function renderRecordDetails(): void {
  const r = S.record, title = $('#docTitle'), sub = $('#docSub'), facts = $('#docFacts'), extra = $('#docExtra');
  if (!r) {
    title.textContent = 'No record selected';
    sub.textContent = 'Choose a record in the results to open its case file and see its details.';
    facts.innerHTML = '';
    return;
  }
  title.textContent = r.last + ', ' + r.first;
  sub.textContent = 'Docket ' + r.docket + ' ' + DOT + ' ' + r.caseType;
  const rows: [string, string][] = [['Date issued', r.issued ? fmtDate(r.issued) : NOKEY], ['Date of death', r.dod ? fmtDate(r.dod) : NOKEY], ['Municipality', r.municipality || NOKEY],
    ['Case file', r.pages ? r.pages + ' ' + plural(r.pages, 'page') : 'None on record']];
  if (r.restricted) rows.push(['Access', 'Restricted. Only accounts allowed all case types see it.']);
  if (r.deletedAt) rows.push(['Deleted', fmtDate(r.deletedAt) + '. It’s out of every search until it’s restored.']);
  const it = cur();
  facts.innerHTML = factsHTML(rows.concat(it && S.seq?.type === 'estate' ? pageFacts(it.page) : []));
  let html = '';
  if (r.pages && S.es.show) html += '<div class="actions"><button type="button" class="btn small" data-open-record="' + esc(r.id) + '">Open case file</button></div>';
  if (can('editRecords') && !r.deletedAt) html += recordFormHTML(r);
  if (can('deleteRecords')) html += r.deletedAt
    ? '<div class="actions"><button type="button" class="btn small" id="restoreRecord">Restore record</button></div>'
    : '<div class="actions"><button type="button" class="btn small danger" id="deleteRecord" data-confirm="Delete it: docket ' + esc(r.docket) + '">Delete record…</button></div>';
  extra.innerHTML = html;
}
function recordFormHTML(r: EstateRow): string {
  const opts = (list: string[], v: string) => Array.from(new Set([...list, v])).filter(Boolean).sort().map(t => '<option' + (t === v ? ' selected' : '') + '>' + esc(t) + '</option>').join('');
  return '<form id="recForm" class="recform" novalidate>' +
    '<span class="label">Edit index record</span>' +
    '<div class="row2"><label class="field" for="rf-docket"><span class="label">Docket no.</span><input type="text" id="rf-docket" class="mono" value="' + esc(r.docket) + '"></label>' +
    '<label class="field" for="rf-type"><span class="label">Case type</span><select id="rf-type">' + opts(office().caseTypes, r.caseType) + '</select></label></div>' +
    '<div class="row2"><label class="field" for="rf-last"><span class="label">Last name</span><input type="text" id="rf-last" value="' + esc(r.last) + '"></label>' +
    '<label class="field" for="rf-first"><span class="label">First name</span><input type="text" id="rf-first" value="' + esc(r.first) + '"></label></div>' +
    '<div class="row2"><label class="field" for="rf-issued"><span class="label">Date issued</span><input type="date" id="rf-issued" value="' + esc(r.issued ?? '') + '"></label>' +
    '<label class="field" for="rf-dod"><span class="label">Date of death</span><input type="date" id="rf-dod" value="' + esc(r.dod ?? '') + '"></label></div>' +
    '<label class="field" for="rf-muni"><span class="label">Municipality</span><select id="rf-muni"><option value=""' + (r.municipality ? '' : ' selected') + '>(none)</option>' + opts(office().municipalities, r.municipality) + '</select></label>' +
    '<p class="err" id="rfErr" hidden></p>' +
    '<div class="actions"><button class="btn primary small" type="submit">Save record</button><button class="btn small" type="button" id="rfUndo">Undo edits</button></div>' +
    '</form>';
}
async function saveRecord(): Promise<void> {
  const r = S.record;
  if (!r) return;
  const err = $('#rfErr'), val = (id: string) => $<HTMLInputElement>('#' + id).value.trim();
  const v = { docket: val('rf-docket'), caseType: val('rf-type'), last: val('rf-last').toUpperCase(), first: val('rf-first').toUpperCase(), issued: val('rf-issued') || null, dod: val('rf-dod') || null, municipality: val('rf-muni') };
  if (!DOCKET_PATTERN.test(v.docket)) { setErr(err, DOCKET_HINT); return; }
  if (!v.last) { setErr(err, 'Enter a last name.'); return; }
  if (v.dod && v.issued && v.dod > v.issued) { setErr(err, 'The date of death can’t be after the date issued.'); return; }
  setErr(err, '');
  try {
    const res = await patch<{ changed: boolean; record: EstateRow }>('/api/v1/estates/' + r.id, v);
    if (!res.changed) { toast('Nothing changed.'); return; }
    Object.assign(r, res.record);
    const row = S.es.rows.find(x => x.id === r.id);
    if (row) Object.assign(row, res.record);
    toast('Record saved. The change is in the log.');
    void loadActivity();
    renderResults(); renderDetails(); renderThumbs(); renderPageLabel();
  } catch (e) { setErr(err, errMsg(e)); }
}

/** Delete and remove act on a second click: the button says what will go, for a few seconds. */
function confirmClick(b: HTMLButtonElement): boolean {
  if (b.dataset.armed) return true;
  const was = b.textContent ?? '';
  b.dataset.armed = '1'; b.textContent = b.dataset.confirm ?? 'Are you sure?';
  setTimeout(() => { if (b.isConnected) { delete b.dataset.armed; b.textContent = was; } }, 5000);
  return false;
}
async function deleteRecord(restore: boolean): Promise<void> {
  const r = S.record;
  if (!r) return;
  try {
    const res = await (restore ? post<{ record: EstateRow }>('/api/v1/estates/' + r.id + '/restore') : del<{ record: EstateRow }>('/api/v1/estates/' + r.id));
    Object.assign(r, res.record);
    S.es.rows = S.es.rows.filter(x => x.id !== r.id);   // it no longer matches this search either way
    S.es.total = Math.max(0, S.es.total - 1);
    S.es.selected = null;
    toast(restore ? 'Record restored.' : 'Record deleted. Tick “Deleted records only” to find it again.');
    void loadActivity();
    if (!restore) { S.record = null; S.seq = null; closeViewer(); }
    showResults();
    renderThumbs(); renderPageLabel(); updateBadges(); updateStaffUI();
  } catch (e) { toast(errMsg(e), 'err'); }
}
async function removeDocument(): Promise<void> {
  const d = S.seq?.document, b = S.seq?.book;
  if (!d || !b) return;
  try {
    await del('/api/v1/documents/' + d.id);
    toast('Removed ' + b.label + ' ' + b.number + ', pages ' + d.first + EN + d.last + '. The change is in the log.');
    void loadActivity();
    showNothing('That document was removed. Its page numbers are free for the right import.');
  } catch (e) { toast(errMsg(e), 'err'); }
}

/* ------------------------------------------------------------ print and export */
function allowedPrinters() { const allow = perms().printers ?? []; return office().printers.filter(p => allow.includes(p.id)); }
const canDownload = () => can('pdf');
function renderPrinters(): void {
  const sel = $<HTMLSelectElement>('#printerSel'), prev = sel.value, ps = allowedPrinters();
  sel.innerHTML = ps.map(p => '<option value="' + esc(p.id) + '">' + esc(p.label) + '</option>').join('');
  if (prev && ps.some(p => p.id === prev)) sel.value = prev;
  sel.disabled = ps.length < 2;
  $('#printerField').hidden = !ps.length;
  $('#copiesField').hidden = !ps.length;
  $('#sendPrint').hidden = !ps.length;
  $('#sendPrint').textContent = ps.length === 1 ? 'Send to ' + ps[0]!.short : 'Send to counter printer';
  $('#dlPdf').hidden = !canDownload();
  $('#rpExport').hidden = !canDownload();
  $('#printNone').hidden = !!(ps.length || canDownload());
  $('#printerHint').textContent = !ps.length ? '' : isGuest() ? 'Guest terminals print to the front counter, where copies are paid for.'
    : ps.length > 1 ? 'Choose which counter printer gets the job.' : 'Jobs go to the ' + ps[0]!.short + '.';
  const cover = $<HTMLInputElement>('#optCover');
  cover.checked = can('coverPage');
  cover.closest('label')!.hidden = !can('coverPage');
  $('#queueBlock').hidden = !ps.length;
  updateFee();
}
function renderPrintBlock(): void {
  const it = cur(), has = !!it;
  ($('#sendPrint') as HTMLButtonElement).disabled = !has;
  ($('#dlPdf') as HTMLButtonElement).disabled = !has;
  ($('#whatView') as HTMLInputElement).disabled = !has; ($('#whatPages') as HTMLInputElement).disabled = !has;
  ($('#optCover') as HTMLInputElement).disabled = !has || !can('coverPage');
  const idx = has && S.seq!.type === 'index';
  $('#whatPagesLabel').textContent = idx ? (it!.section ? 'This section' : 'This page') : 'Pages';
  $('#rangeWrap').hidden = idx || !has;
  updateFee(); updateIiifLine();
}
function resetPrintRange(): void {
  const it = cur(), seq = S.seq;
  if (!it || !seq) return;
  let key: string, from = '', to = '';
  if (seq.type === 'book') { key = 'b' + seq.document!.id; from = seq.items[0]!.label; to = seq.items[seq.items.length - 1]!.label; }
  else if (seq.type === 'estate') { key = 'e' + seq.record!.id; from = '1'; to = String(seq.items.length); }
  else key = 'i' + it.page.id;
  if (key !== S.rangeKey) { S.rangeKey = key; $<HTMLInputElement>('#pFrom').value = from; $<HTMLInputElement>('#pTo').value = to; setErr($('#printErr'), ''); }
  updateFee();
}
function rangeParts(report: boolean): { f: string; t: string; items: SeqItem[] } | null {
  const seq = S.seq!;
  const f = $<HTMLInputElement>('#pFrom').value.trim(), t = $<HTMLInputElement>('#pTo').value.trim();
  const key = (s: string) => (seq.type === 'book' ? s.toUpperCase().replace(/^(\d{1,4})([A-Z]?)$/, (_m, d: string, x: string) => d.padStart(4, '0') + x) : String(Number(s)));
  const labels = seq.items.map(i => (seq.type === 'book' ? i.label : String(i.n)));
  const a = labels.indexOf(key(f)), b = labels.indexOf(key(t));
  if (a < 0 || b < 0 || a > b) {
    if (report) setErr($('#printErr'), 'Enter pages from ' + labels[0] + ' to ' + labels[labels.length - 1] + ', with the first page no higher than the last.');
    return null;
  }
  return { f: labels[a]!, t: labels[b]!, items: seq.items.slice(a, b + 1) };
}
function updateFee(): void {
  const it = cur(), el = $('#feeLine');
  if (!it || !allowedPrinters().length || !office().feeCents) { el.textContent = ''; return; }
  let n = 1;
  if (!$<HTMLInputElement>('#whatView').checked && S.seq!.type !== 'index') { const r = rangeParts(false); n = r ? r.items.length : 0; }
  const copies = Math.max(1, Math.min(20, Number($<HTMLInputElement>('#copies').value) || 1)), fee = office().feeCents;
  el.textContent = n ? 'Copy fee: ' + (n * copies) + ' ' + plural(n * copies, 'page') + ' × ' + money(fee) + ' = ' + money(n * copies * fee) + ', collected at the counter. The cover page is free.' : '';
}
function updateIiifLine(): void {
  const el = $('#iiifLine'), p = curPage();
  if (!p || !$<HTMLInputElement>('#whatView').checked || !can('originals')) { el.hidden = true; return; }
  const r = currentRegion();
  if (!r) { el.hidden = true; return; }
  el.innerHTML = '<b>Image request for this area</b>' + esc(p.iiif + '/' + r.x + ',' + r.y + ',' + r.w + ',' + r.h + '/max/' + r.rotation + '/' + (S.gray ? 'gray' : 'default') + '.jpg');
  el.hidden = false;
}
interface Selection { body: Record<string, unknown>; pages: number; title: string }
function getSelection(): Selection | null {
  const it = cur(), seq = S.seq;
  if (!it || !seq) return null;
  const base = seq.type === 'book' ? seq.book!.label + ' ' + seq.book!.number : seq.type === 'index' ? seq.index!.label + ' Book ' + seq.volumeBook
    : 'Docket ' + seq.record!.docket + ' ' + seq.record!.last + ', ' + seq.record!.first;
  const common = { cover: can('coverPage') && $<HTMLInputElement>('#optCover').checked, printedFor: $<HTMLInputElement>('#printedFor').value.trim(),
    asPublic: S.viewAsPublic, invert: S.invert, gray: S.gray };
  if ($<HTMLInputElement>('#whatView').checked) {
    const r = currentRegion();
    if (!r) { setErr($('#printErr'), 'Zoom so part of the page is on screen, then try again.'); return null; }
    setErr($('#printErr'), '');
    return { pages: 1, title: pageName(it), body: { ...common, region: { pageId: it.page.id, ...r }, title: pageName(it), subtitle: 'Area on screen at full resolution (' + r.w + ' × ' + r.h + ' px)' } };
  }
  if (seq.type === 'index') {
    setErr($('#printErr'), '');
    return { pages: 1, title: pageName(it), body: { ...common, pages: [it.page.id], rotations: { [it.page.id]: rotation() }, title: pageName(it), subtitle: seq.seriesLabel + (it.section ? ' ' + DOT + ' ' + sectionTag(it.section, seq.index!.method, true) : '') } };
  }
  const r = rangeParts(true);
  if (!r) return null;
  setErr($('#printErr'), '');
  const title = base + ', ' + (r.f === r.t ? 'page ' + r.f : 'pages ' + r.f + EN + r.t);
  const subtitle = seq.type === 'book' ? seq.book!.label.replace(/ Book$/, '') + ' books ' + DOT + ' ' + office().name : seq.record!.caseType + ' ' + DOT + ' ' + office().name;
  const rotations: Record<string, number> = {};
  for (const i of r.items) rotations[i.page.id] = i === it && hasImage() ? rotation() : i.page.rotation || 0;
  return { pages: r.items.length, title, body: { ...common, pages: r.items.map(i => i.page.id), rotations, title, subtitle } };
}
async function downloadPdf(): Promise<void> {
  const btn = $<HTMLButtonElement>('#dlPdf');
  if (btn.classList.contains('busy') || !can('pdf')) return;
  const sel = getSelection();
  if (!sel) return;
  btn.classList.add('busy'); btn.textContent = 'Preparing PDF…';
  try {
    const f = await fetchFile('POST', '/api/v1/copies/pdf', sel.body);
    saveBlob(f.blob, f.filename);
    toast('PDF saved.');
  } catch (e) { toast(errMsg(e), 'err'); } finally { btn.classList.remove('busy'); btn.textContent = 'Download PDF'; }
}
async function sendToPrinter(): Promise<void> {
  const sel = getSelection();
  if (!sel) return;
  const nameEl = $<HTMLInputElement>('#printedFor'), forName = nameEl.value.trim();
  if (isGuest() && !forName) {
    setErr($('#printErr'), 'Enter a name for the pickup slip.');
    nameEl.setAttribute('aria-invalid', 'true'); nameEl.focus();
    return;
  }
  nameEl.removeAttribute('aria-invalid');
  const ps = allowedPrinters();
  if (!ps.length) return;
  const p = ps.find(x => x.id === $<HTMLSelectElement>('#printerSel').value) ?? ps[0]!;
  const copies = Math.max(1, Math.min(20, Number($<HTMLInputElement>('#copies').value) || 1));
  const btn = $<HTMLButtonElement>('#sendPrint');
  btn.disabled = true;
  try {
    const job = await post<PrintJob>('/api/v1/print', { ...sel.body, printerId: p.id, copies });
    S.jobs = [job, ...S.jobs.filter(j => j.id !== job.id)];
    renderJobs();
    toast('Job ' + job.number + ' sent. It prints on the ' + p.short + ' in a moment.');
    pollJobs();
  } catch (e) { setErr($('#printErr'), errMsg(e)); } finally { btn.disabled = !cur(); }
}
let jobTimer: ReturnType<typeof setTimeout> | undefined;
async function loadJobs(): Promise<void> {
  if (!allowedPrinters().length) return;
  try { S.jobs = (await get<{ jobs: PrintJob[] }>('/api/v1/print/jobs')).jobs; renderJobs(); } catch { /* keep the old list */ }
}
function pollJobs(): void {
  clearTimeout(jobTimer);
  jobTimer = setTimeout(async () => {
    await loadJobs();
    if (S.jobs.some(j => ['queued', 'rendering', 'ready', 'printing'].includes(j.status))) pollJobs();
  }, 2500);
}
function renderJobs(): void {
  const list = S.jobs.slice(0, 5), el = $('#jobList');
  if (!list.length) { el.innerHTML = '<li class="empty">No print jobs yet. Jobs appear here as the office print agent picks them up.</li>'; return; }
  const LABEL: Record<PrintJob['status'], string> = { queued: 'Queued', rendering: 'Preparing', ready: 'Waiting for the printer', printing: 'Printing', printed: 'Printed', failed: 'Not printed' };
  el.innerHTML = list.map(j => {
    const st = j.status, step = st === 'queued' || st === 'rendering' ? 1 : st === 'ready' || st === 'printing' ? 2 : 3;
    return '<li class="job"><span class="jid">Job ' + esc(j.number) + ' ' + DOT + ' ' + esc(timeLabel(j.createdAt)) + '</span>' +
      '<span class="pill ' + (st === 'printed' ? 'ok' : st === 'failed' ? 'red' : 'warn') + '">' + LABEL[st] + '</span>' +
      '<span class="jt">' + esc(j.title) + '</span>' +
      '<span class="js">' + j.pages + ' ' + plural(j.pages, 'page') + (j.cover ? ' + cover' : '') + ' ' + DOT + ' ' + esc(j.printer?.label ?? '') + ' ' + DOT + ' for ' + esc(j.forName) + (j.message ? ' ' + DOT + ' ' + esc(j.message) : '') + '</span>' +
      (st !== 'failed' ? '<span class="track" aria-hidden="true"><span class="on"></span><span class="' + (step > 1 ? 'on' : '') + '"></span><span class="' + (step > 2 ? 'on' : '') + '"></span></span>' : '') +
      (['queued', 'rendering', 'ready'].includes(st) ? '<span class="js"><button type="button" class="linkish" data-cancel-job="' + esc(j.id) + '">Cancel</button></span>' : '') + '</li>';
  }).join('');
}

/* ------------------------------------------------------------ staff tools */
async function loadActivity(): Promise<void> {
  if (!can('originals') && !can('editRecords')) return;
  try { S.activity = (await get<{ entries: ActivityEntry[] }>('/api/v1/activity?limit=6')).entries; renderAudit(); } catch { /* keep the old list */ }
}
function renderAudit(): void {
  const el = $('#auditList'), list = S.activity;
  el.innerHTML = list.length ? list.map(a => '<li><time>' + esc(timeLabel(a.at)) + '</time><span>' + esc(a.text) + ' (' + esc(a.actor) + ')</span></li>').join('')
    : '<li class="empty">No changes yet. Redactions, rotations and record edits are logged here.</li>';
}
function updateStaffUI(): void {
  const staff = can('originals');
  $('#staffBlock').hidden = !staff;
  $('#redactTool').hidden = !staff;
  $('#importBtn').hidden = !can('importDocs');
  $('#esDeletedWrap').hidden = !can('deleteRecords');
  if (!staff) return;
  const p = curPage(), n = p ? p.redactions : 0, dirty = isDirty();
  const rs = $('#redactStatus');
  if (!p) rs.textContent = 'Open a page to use the staff tools.';
  else if (S.viewAsPublic) rs.textContent = n ? 'You’re viewing the public copy, with ' + n + ' ' + plural(n, 'box', 'boxes') + ' burned in. Turn this off to edit.' : 'This page has no redactions, so the public sees the original.';
  else if (dirty) rs.textContent = 'You have changes that aren’t on the public copy yet.';
  else rs.textContent = n ? n + ' ' + plural(n, 'box is', 'boxes are') + ' burned into the public copy. Choose Redact in the toolbar to change them.' : 'No redactions on this page. Choose Redact in the toolbar and drag over anything the public shouldn’t see.';
  $<HTMLButtonElement>('#applyRedact').disabled = !dirty;
  $<HTMLButtonElement>('#discardRedact').disabled = !dirty;
  $<HTMLInputElement>('#viewPublic').checked = S.viewAsPublic;
  $<HTMLInputElement>('#viewPublic').disabled = !p || (!!n && !p.publicReady);
  $<HTMLButtonElement>('#redactTool').disabled = S.viewAsPublic || !p;
  const savedR = p ? (p.rotation || 0) : 0, curR = hasImage() ? rotation() : savedR;
  $('#rotStatus').textContent = !p ? '' : curR === savedR ? 'Rotation: this page opens at ' + savedR + DEG + ' for everyone.' : 'Rotation: showing ' + curR + DEG + '; the saved rotation is ' + savedR + DEG + '.';
  $<HTMLButtonElement>('#saveRot').disabled = !p || curR === savedR;
  $<HTMLButtonElement>('#delBox').disabled = !boxes?.selected() || S.tool !== 'redact';
  updateClearBtn();
}

/* ------------------------------------------------------------ index books */
const volumes = new Map<string, IndexVolumeResponse>();
async function volume(ix: IndexBookInfo, series: string, book: string): Promise<IndexVolumeResponse> {
  const k = ix.id + '|' + series + '|' + book;
  let v = volumes.get(k);
  if (!v) { v = await get<IndexVolumeResponse>(`/api/v1/indexes/${ix.id}/volumes/${encodeURIComponent(series)}/${book}`); volumes.set(k, v); }
  return v;
}
function renderIdxChips(): void {
  if (!hasModule('index') || !office().indexes.length) return;
  const f = form(), ix = indexById(f.which) ?? office().indexes[0]!;
  const seq = S.seq?.type === 'index' && S.seq.index?.id === ix.id ? S.seq : null;
  const onItem = seq ? cur() : null;
  const book = seq?.volumeBook ?? ix.book, series = seq?.series ?? ix.volumes[0]?.series ?? '';
  const sections = seq ? seq.items.filter(i => i.section).map(i => i.section!) : ix.sections.filter(s => s.pageId);
  $('#idxBrowseLabel').textContent = book ? 'Browse ' + ix.label + ', Book ' + book : 'No books of this index are scanned yet';
  // several scanned books: a picker (series and book), since the chips show one book at a time
  const picker = $('#idxBooks') as HTMLSelectElement;
  picker.hidden = ix.volumes.length < 2;
  if (!picker.hidden) {
    const sl = (code: string) => ix.series.find(s => s.id === code)?.label ?? code;
    picker.innerHTML = ix.volumes.map(v => '<option value="' + esc(v.series + '|' + v.book) + '"' + (v.series === series && v.book.toLowerCase() === String(book).toLowerCase() ? ' selected' : '') + '>Book ' + esc(v.book) + ' (' + esc(v.surnames) + ') ' + DOT + ' ' + esc(sl(v.series)) + '</option>').join('');
  }
  $('#idxChips').innerHTML = !book ? '' : ['<button type="button" class="chip" data-sec="guide"' + (onItem && !onItem.section ? ' aria-current="true"' : '') + '>Section guide</button>']
    .concat(sections.map(s => '<button type="button" class="chip" data-sec="' + s.section + '"' + (onItem?.section?.section === s.section ? ' aria-current="true"' : '') + '>' +
      pad4(s.section) + '<small>' + esc(sectionTag(s, ix.method)) + '</small></button>')).join('');
}
async function openIndexSection(sec: string, where?: { series: string; book: string }): Promise<void> {
  const f = form(), ix = indexById(f.which);
  if (!ix) return;
  const series = where?.series ?? (S.seq?.type === 'index' && S.seq.index?.id === ix.id ? S.seq.series! : ix.volumes[0]?.series ?? f.series);
  const book = where?.book ?? (S.seq?.type === 'index' && S.seq.index?.id === ix.id ? S.seq.volumeBook! : ix.book);
  if (!book) return;
  try {
    if (!(S.seq?.type === 'index' && S.seq.index?.id === ix.id && S.seq.series === series && S.seq.volumeBook === book)) {
      S.seq = indexSeq(ix, await volume(ix, series, book));
      renderThumbs();
    }
    const i = sec === 'guide' ? S.seq.items.findIndex(x => !x.section) : S.seq.items.findIndex(x => x.section?.section === Number(sec));
    await openIdx(Math.max(0, i));
  } catch (e) { toast(errMsg(e), 'err'); }
}
async function runIndexSearch(): Promise<boolean> {
  const f = form(), ix = indexById(f.which), err = $('#idxErr');
  if (!ix) return false;
  const series = $<HTMLSelectElement>('#idxSeries').value, raw = $<HTMLInputElement>('#idxLast').value.trim(), m = methodInfo(ix.method);
  if (!raw) return setErr(err, m.usesCorporation ? 'Enter a surname or corporation name.' : 'Enter a surname.');
  if (!/^[A-Za-z][A-Za-z '\-.&,]*$/.test(raw)) return setErr(err, 'Use letters. Spaces, hyphens and apostrophes are fine.');
  const corp = m.usesCorporation && $<HTMLInputElement>('#idxCorp').checked, init = $<HTMLInputElement>('#idxInit').value.trim().toUpperCase();
  if (m.needsInitial && !corp && !/^[A-Z]$/.test(init)) return setErr(err, m.usesCorporation ? 'Enter the first letter of the given name, or tick Corporation.' : 'Enter the first letter of the given name.');
  if (init && !/^[A-Z]$/.test(init)) return setErr(err, 'The given-name initial is one letter.');
  setErr(err, '');
  let r: IndexLookupResponse;
  try { r = await post<IndexLookupResponse>(`/api/v1/indexes/${ix.id}/lookup`, { series, surname: raw, initial: init, corporation: corp }); }
  catch (e) { return setErr(err, errMsg(e)); }
  const open = r.status === 'opened';
  const headline = 'Book ' + (r.volumeBook ?? r.book) + (r.section ? ' ' + DOT + ' Section ' + pad4(r.section) : '');
  $('#idxResult').innerHTML = '<div class="card' + (open ? '' : ' warn') + '"><div class="answer"><strong>' + esc(headline) + '</strong>' +
    '<span class="pill ' + (open ? 'ok' : 'warn') + '">' + (open ? 'Opened' : 'Not scanned') + '</span></div>' +
    '<ol class="steps">' + r.steps.map(s => '<li>' + stepHTML(s) + '</li>').join('') + '</ol>' + (r.message ? '<p class="hint">' + esc(r.message) + '</p>' : '') + '</div>';
  if (open && r.section && r.volumeBook) await openIndexSection(String(r.section), { series: r.series, book: r.volumeBook });
  return true;
}

/* ------------------------------------------------------------ book and page */
async function runBookPage(type: string, bookStr: string, pageStr: string, o: { quiet?: boolean; atEnd?: boolean } = {}): Promise<boolean> {
  const err = $('#bpErr');
  bookStr = String(bookStr).trim(); pageStr = String(pageStr).trim();
  if (!/^([A-Za-z]{1,2})?\d{1,4}$/.test(bookStr)) return setErr(err, 'Enter the book number, up to four digits.');
  if (!/^\d{1,4}[A-Za-z]?$/.test(pageStr)) return setErr(err, 'Enter the page number, up to four digits.');
  setErr(err, '');
  let r: BookPageResponse;
  try { r = await get<BookPageResponse>(`/api/v1/books/${encodeURIComponent(type)}/${encodeURIComponent(bookStr)}?page=${encodeURIComponent(pageStr)}`); }
  catch (e) {
    if (o.quiet) { toast(errMsg(e), 'err'); return false; }
    $('#bpResult').innerHTML = '<div class="card warn"><p class="hint">' + esc(errMsg(e)) + '</p></div>';
    return false;
  }
  S.seq = bookSeq(r);
  renderThumbs();
  await openIdx(o.atEnd ? S.seq.items.length - 1 : o.quiet ? 0 : r.startIndex);
  const d = r.document, n = r.pages.length, pg = r.pages[r.startIndex]?.label ?? pageStr;
  if (!o.quiet) {
    $('#bpResult').innerHTML = '<div class="card"><div class="answer"><strong>' + esc(r.book.label) + ' ' + esc(r.book.number) + ' ' + DOT + ' Page ' + esc(pg) + '</strong><span class="pill ok">Opened</span></div>' +
      '<p class="hint">' + (pg === d.first ? (n > 1 ? 'This page starts a ' + n + '-page document (' + d.first + EN + d.last + ').' : 'A one-page document.') : 'This page is inside the document at ' + d.first + EN + d.last + '.') +
      ' The print range is set to the whole document.</p></div>';
  }
  return true;
}
function openBookPageFromShortcut(spec: string): void {
  const [type, book, page] = spec.split('|') as [string, string, string];
  saveMem();
  const f = form();
  f.bpType = type; f.bpBook = book; f.bpPage = page;
  S.mem[officeSlug()]!.bookpage = null;
  S.tab[officeSlug()] = 'bookpage';
  renderTabs(); applyForms(); void restoreTab();
}

/* ------------------------------------------------------------ estate records: large results, searched and paged by the server */
function renderEstateFilters(): void {
  $('#esType').innerHTML = '<option value="">All case types</option>' + office().caseTypes.map(t => '<option>' + esc(t) + '</option>').join('');
  $('#esMuni').innerHTML = '<option value="">All municipalities</option>' + office().municipalities.map(t => '<option>' + esc(t) + '</option>').join('');
}
function readEstateForm(): EstateQuery {
  const v = (id: string) => $<HTMLInputElement>('#' + id).value.trim();
  return { last: v('esLast').toUpperCase(), sounds: $<HTMLInputElement>('#esSounds').checked, first: v('esFirst').toUpperCase(), docket: v('esDocket'), type: v('esType'), muni: v('esMuni'), from: v('esFrom'), to: v('esTo'),
    deleted: can('deleteRecords') && $<HTMLInputElement>('#esDeleted').checked };
}
function clearEstateForm(): void {
  for (const id of ['#esLast', '#esFirst', '#esDocket', '#esFrom', '#esTo']) $<HTMLInputElement>(id).value = '';
  $<HTMLInputElement>('#esSounds').checked = false; $<HTMLInputElement>('#esDeleted').checked = false; $<HTMLSelectElement>('#esType').value = ''; $<HTMLSelectElement>('#esMuni').value = '';
}
function describeQuery(q: EstateQuery): string {
  const parts: string[] = [];
  if (q.last) parts.push((q.sounds ? 'sounds like ' : 'last name ') + q.last);
  if (q.first) parts.push('first name ' + q.first);
  if (q.docket) parts.push('docket ' + q.docket);
  if (q.type) parts.push(q.type);
  if (q.muni) parts.push(q.muni);
  if (q.from || q.to) parts.push('died ' + (q.from && q.to ? q.from + EN + q.to : q.from ? 'in or after ' + q.from : 'in or before ' + q.to));
  if (q.deleted) parts.push('deleted records');
  return parts.length ? parts.join(' ' + DOT + ' ') : 'all records';
}
function estateParams(q: EstateQuery, page = S.es.page): string {
  const p = new URLSearchParams();
  if (q.last) p.set('last', q.last);
  if (q.sounds) p.set('soundsLike', '1');
  if (q.first) p.set('first', q.first);
  if (q.docket) p.set('docket', q.docket);
  if (q.type) p.set('caseType', q.type);
  if (q.muni) p.set('municipality', q.muni);
  if (q.from) p.set('diedFrom', q.from);
  if (q.to) p.set('diedTo', q.to);
  if (q.deleted) p.set('deleted', '1');
  p.set('sort', S.es.sort.key); p.set('dir', S.es.sort.dir); p.set('page', String(page)); p.set('size', String(S.es.size));
  return p.toString();
}
async function fetchEstates(page: number): Promise<boolean> {
  const q = S.es.q!;
  S.es.loading = true;
  try {
    const r = await get<EstateSearchResponse>('/api/v1/estates?' + estateParams(q, page));
    Object.assign(S.es, { rows: r.rows, total: r.total, restrictedHidden: r.restrictedHidden, page: r.page, ran: true });
    return true;
  } catch (e) { setErr($('#esErr'), errMsg(e)); return false; } finally { S.es.loading = false; }
}
async function runEstateSearch(): Promise<boolean> {
  const q = readEstateForm(), err = $('#esErr');
  if ((q.from && !/^\d{4}$/.test(q.from)) || (q.to && !/^\d{4}$/.test(q.to))) return setErr(err, 'Enter years as four digits, like 1986.');
  if (q.from && q.to && q.from > q.to) return setErr(err, 'The first year should be no later than the second.');
  if (q.sounds && !q.last) return setErr(err, 'Enter a last name to use Sounds like.');
  setErr(err, '');
  S.es.q = q;
  if (!(await fetchEstates(1))) return false;
  S.es.show = true;
  const w = $maybe('.rp-table-wrap'); if (w) w.scrollTop = 0;
  renderResults(); renderDetails();
  return true;
}
function renderResults(): void {
  const pane = $('#resultsPane'), onTab = S.tab[officeSlug()] === 'estates' && hasModule('estates');
  const visible = onTab && S.es.show && S.es.ran;
  pane.hidden = !visible;
  for (const sel of ['.toolbar', '.stage-wrap', '#thumbs']) { const el = $maybe(sel); if (el) el.inert = visible; }
  $('#backToResults').hidden = !(onTab && !S.es.show && S.es.ran);
  renderEstateSummary();
  if (!visible) return;
  const es = S.es, q = es.q!, pages = Math.max(1, Math.ceil(es.total / es.size));
  $('#rpCount').textContent = num(es.total) + ' ' + plural(es.total, 'record') + ' ' + DOT + ' ' + describeQuery(q) +
    (es.restrictedHidden ? ' ' + DOT + ' ' + num(es.restrictedHidden) + ' restricted, hidden from the public' : '');
  const hint = $('#rpHint');
  let hintText = '';
  if (q.sounds && es.total) {
    const names = Array.from(new Set(es.rows.map(r => r.last))).sort();
    hintText = 'Sounds like ' + q.last + ' matched ' + names.slice(0, 10).join(', ') + (names.length > 10 ? ' and more' : '') + (pages > 1 ? ' on this page of results' : '') + '.';
  } else if (es.total > 500 && !q.last && !q.docket && !q.first) {
    hintText = 'That’s a broad search. Add a last name, first name or years of death to narrow it. Results come back one page at a time, so even large searches stay quick.';
  }
  hint.textContent = hintText; hint.hidden = !hintText;
  const cols: [string, string][] = [['docket', 'Docket'], ['last', 'Name'], ['caseType', 'Case type'], ['dod', 'Died'], ['issued', 'Issued'], ['municipality', 'Municipality']];
  $('#rpHead').innerHTML = '<tr>' + cols.map(c => {
    const on = es.sort.key === c[0];
    return '<th scope="col" aria-sort="' + (on ? (es.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') + '"><button type="button" data-sort="' + c[0] + '">' + esc(c[1]) + '</button></th>';
  }).join('') + '</tr>';
  const selId = S.record?.id ?? es.selected;
  $('#rpBody').innerHTML = es.rows.length ? es.rows.map(r =>
    '<tr data-id="' + esc(r.id) + '" tabindex="0"' + (selId === r.id ? ' aria-current="true"' : '') + '>' +
    '<td class="dk">' + esc(r.docket) + '</td><td class="nm"><span class="n">' + esc(r.last) + ', ' + esc(r.first) + '</span>' +
    (r.pages ? '' : ' <span class="pill">No case file</span>') + (r.restricted ? ' <span class="pill red">Restricted</span>' : '') + '</td>' +
    '<td>' + esc(r.caseType) + '</td><td class="nw">' + esc(r.dod ? fmtDate(r.dod) : NOKEY) + '</td><td class="nw">' + esc(r.issued ? fmtDate(r.issued) : NOKEY) + '</td>' +
    '<td class="nw">' + esc(r.municipality) + '</td></tr>').join('')
    : '<tr><td colspan="6" class="rp-empty">' + (q.sounds ? 'No surnames sound like ' + esc(q.last) + '. Sounds like compares whole surnames, so enter the full name.'
      : 'No records match. Try fewer filters, or tick Sounds like to catch spelling variants.') + '</td></tr>';
  const start = (es.page - 1) * es.size, from = es.total ? start + 1 : 0, to = Math.min(es.total, start + es.size);
  const dis = (c: boolean) => (c ? ' disabled' : '');
  $('#rpPager').innerHTML = '<span>Showing ' + num(from) + EN + num(to) + ' of ' + num(es.total) + '</span><span class="pg">' +
    '<button type="button" class="btn small" data-pg="first"' + dis(es.page <= 1) + '>First</button>' +
    '<button type="button" class="btn small" data-pg="prev"' + dis(es.page <= 1) + '>Previous</button>' +
    '<span>Page ' + num(es.page) + ' of ' + num(pages) + '</span>' +
    '<button type="button" class="btn small" data-pg="next"' + dis(es.page >= pages) + '>Next</button>' +
    '<button type="button" class="btn small" data-pg="last"' + dis(es.page >= pages) + '>Last</button></span>';
  $<HTMLSelectElement>('#rpSize').value = String(es.size);
}
function renderEstateSummary(): void {
  const el = $('#esSummary'), es = S.es;
  if (!es.ran) { el.innerHTML = ''; return; }
  const pages = Math.max(1, Math.ceil(es.total / es.size));
  el.innerHTML = '<div class="card"><div class="answer"><strong>' + num(es.total) + ' ' + plural(es.total, 'match', 'matches') + '</strong>' +
    '<span class="pill">Page ' + num(es.page) + ' of ' + num(pages) + '</span></div>' +
    (es.show ? '<p class="hint">Choose a record to open its case file.</p>' : '<div class="actions"><button type="button" class="btn small" id="esShow">Show results</button></div>') + '</div>';
}
async function goToResultsPage(p: number): Promise<void> {
  const pages = Math.max(1, Math.ceil(S.es.total / S.es.size));
  if (!S.es.q || S.es.loading) return;
  if (await fetchEstates(Math.max(1, Math.min(pages, p)))) { renderResults(); $('.rp-table-wrap').scrollTop = 0; }
}
function showResults(): void {
  S.es.show = true;
  renderResults(); renderDetails();
  const tr = $maybe('#rpBody tr[aria-current="true"]');
  if (tr) tr.focus({ preventScroll: false });
}
async function selectRecord(id: string, fromKeyboard = false): Promise<void> {
  const row = S.es.rows.find(x => x.id === id) ?? (S.record?.id === id ? S.record : null);
  if (!row) return;
  S.es.selected = id;
  S.record = row;
  if (row.pages) {
    let rec: EstateRecordResponse;
    try { rec = await get<EstateRecordResponse>('/api/v1/estates/' + id); } catch (e) { toast(errMsg(e), 'err'); return; }
    S.es.show = false;
    S.seq = estateSeq(rec);
    renderResults(); renderThumbs(); await openIdx(0);
    if (fromKeyboard) $('#backToResults').focus();
  } else {
    showNothing('No case file is on record for this docket.');
    renderResults();
    if (fromKeyboard) $maybe('#rpBody tr[data-id="' + id + '"]')?.focus();
  }
}
async function exportCsv(): Promise<void> {
  if (!can('pdf')) return;
  if (!S.es.ran || !S.es.total) { toast('Run a search with results first.'); return; }
  try {
    const p = new URLSearchParams(estateParams(S.es.q!, 1)); p.delete('page'); p.delete('size');
    const f = await fetchFile('GET', '/api/v1/estates.csv?' + p.toString());
    saveBlob(f.blob, f.filename);
    toast('Exported ' + num(S.es.total) + ' ' + plural(S.es.total, 'record') + '.');
  } catch (e) { toast(errMsg(e), 'err'); }
}

/* ------------------------------------------------------------ offices, tabs and forms */
function defaultForm(o: OfficeInfo): (typeof S.forms)[string] {
  const ix = o.indexes[0];
  return { which: ix?.id ?? '', series: ix?.volumes[0]?.series ?? ix?.series[0]?.id ?? '', last: '', init: '', corp: false, bpType: o.bookTypes[0]?.id ?? '', bpBook: '', bpPage: '1' };
}
function modules(): Tab[] { return (['estates', 'index', 'bookpage'] as Tab[]).filter(m => office().modules.includes(m)).sort((a, b) => office().modules.indexOf(a) - office().modules.indexOf(b)); }
function renderTabs(): void {
  const tabs = modules(), active = S.tab[officeSlug()];
  $('#tabs').innerHTML = tabs.map(t => '<button type="button" role="tab" class="tab" id="tab-' + t + '" aria-controls="tp-' + t + '" aria-selected="' + (t === active) + '" tabindex="' + (t === active ? '0' : '-1') + '" data-tab="' + t + '">' + esc(MODULE_LABELS[t]) + '</button>').join('');
  for (const id of ['index', 'bookpage', 'estates'] as Tab[]) $('#tp-' + id).hidden = !(id === active && tabs.includes(id));
}
function saveForms(): void {
  const f = form();
  f.series = $<HTMLSelectElement>('#idxSeries').value || f.series; f.last = $<HTMLInputElement>('#idxLast').value; f.init = $<HTMLInputElement>('#idxInit').value; f.corp = $<HTMLInputElement>('#idxCorp').checked;
  f.bpType = $<HTMLSelectElement>('#bpType').value || f.bpType; f.bpBook = $<HTMLInputElement>('#bpBook').value; f.bpPage = $<HTMLInputElement>('#bpPage').value;
}
function applyForms(): void {
  const o = office(), f = form(), ix = indexById(f.which) ?? o.indexes[0];
  if (ix) {
    $('#idxWhich').innerHTML = o.indexes.map(w => '<button type="button" data-which="' + esc(w.id) + '" aria-pressed="' + (w.id === ix.id) + '">' + esc(w.short) + '</button>').join('');
    $('#idxWhich').closest<HTMLElement>('.field')!.hidden = o.indexes.length < 2;
    $('#idxSeries').innerHTML = ix.series.map(s => '<option value="' + esc(s.id) + '">' + esc(s.label) + '</option>').join('');
    $<HTMLSelectElement>('#idxSeries').value = f.series || ix.series[0]?.id || '';
    const m = methodInfo(ix.method);
    $('#idxInitRow').hidden = !m.usesInitial;
    $('#idxInitLabel').textContent = m.needsInitial ? 'First initial' : 'First initial (if the book divides by it)';
    $('#idxCorp').closest<HTMLElement>('label')!.hidden = !m.usesCorporation;
    $('#idxLastLabel').textContent = m.usesCorporation ? 'Surname or corporation' : 'Surname';
    $('#idxMethodNote').textContent = ix.label + ' uses the ' + m.short + ' method: ' + m.summary;
  }
  $<HTMLInputElement>('#idxLast').value = f.last; $<HTMLInputElement>('#idxInit').value = f.init; $<HTMLInputElement>('#idxCorp').checked = f.corp; $<HTMLInputElement>('#idxInit').disabled = f.corp;
  $('#bpType').innerHTML = o.bookTypes.map(b => '<option value="' + esc(b.id) + '">' + esc(b.plural) + '</option>').join('');
  $<HTMLSelectElement>('#bpType').value = f.bpType; $<HTMLInputElement>('#bpBook').value = f.bpBook; $<HTMLInputElement>('#bpPage').value = f.bpPage;
  renderTryHints();
  renderIdxChips();
}
/** Shortcuts into the Sample County records, shown only on the demonstration tenant. */
function renderTryHints(): void {
  const sample = !!S.site?.tenant.sample, o = office();
  const it = $('#idxTry'), bt = $('#bpTry'), et = $('#esTry');
  it.innerHTML = ''; bt.innerHTML = ''; et.hidden = true;
  if (!sample) return;
  const idx = o.indexes.map(ix => ix.method === 'russell'
    ? [[ix.id, 'Norman', 'J', 'Norman, J'], [ix.id, 'Nelson', 'A', 'Nelson, A']] : [[ix.id, 'Nelson', '', 'Nelson'], [ix.id, 'Norton', '', 'Norton (filed on its own)'], [ix.id, 'Newman', '', 'Newman']]).flat().slice(0, 4);
  if (idx.length) it.innerHTML = 'Try ' + idx.map(t => '<button type="button" class="linkish" data-try="' + esc(t.slice(0, 3).join('|')) + '">' + esc(t[3]!) + '</button>').join(', ') + '.';
  const bps = o.bookTypes.map(b => b.id === 'deed' ? [['deed', '300', '7', 'Deed 300, page 7'], ['deed', '300', '9', 'page 9 (scanned sideways)']] : b.id === 'mortgage' ? [['mortgage', '112', '1', 'Mortgage 112, page 1 (microfilm negative)']] : b.id === 'will' ? [['will', '45', '1', 'Will Book 45, page 1']] : []).flat();
  if (bps.length) bt.innerHTML = 'Try ' + bps.map(t => '<button type="button" class="linkish" data-bp="' + esc(t.slice(0, 3).join('|')) + '">' + esc(t[3]!) + '</button>').join(', ') + '.';
  if (hasModule('estates')) {
    et.innerHTML = 'Try <button type="button" class="linkish" data-es="last=Nolan">Nolan</button> (docket 1986-0233 has a redaction), <button type="button" class="linkish" data-es="sounds=Smith">Smith, sounds like</button> or <button type="button" class="linkish" data-es="type=Guardianship">Guardianship</button> (restricted; staff only).';
    et.hidden = false;
  }
}
function saveMem(): void {
  saveForms();
  S.mem[officeSlug()] ??= {};
  S.mem[officeSlug()]![S.tab[officeSlug()]!] = { seq: S.seq, idx: S.idx, record: S.record };
}
function showNothing(msg: string): void {
  S.seq = null;
  closeViewer();
  stageMessage(msg);
  renderThumbs(); renderPageLabel(); renderDetails(); updateBadges(); updateStaffUI();
}
async function restoreTab(): Promise<void> {
  const tab = S.tab[officeSlug()]!;
  const m = S.mem[officeSlug()]?.[tab];
  S.rangeKey = null;
  hideStageMessage();
  S.record = null;
  if (m && (m.seq || m.record)) {
    S.seq = m.seq; S.idx = m.idx; S.record = m.record;
    renderThumbs();
    if (S.seq) await openIdx(S.idx);
    else showNothing(tab === 'estates' ? 'No case file is on record for this docket.' : 'Search to open a page.');
  } else if (tab === 'index') {
    S.seq = null;
    const f = form();
    if (f.last) await runIndexSearch();
    if (!S.seq) { showNothing('Search an index book to open a section.'); if (office().indexes.length) await openIndexSection('guide').catch(() => undefined); }
  } else if (tab === 'bookpage') {
    const f = form();
    S.seq = null;
    if (f.bpBook) await runBookPage(f.bpType, f.bpBook, f.bpPage);
    if (!S.seq) showNothing('Enter a book and page to open it.');
  } else {
    showNothing('Choose a record in the results to open its case file.');
    if (!S.es.ran) { S.es.q = readEstateForm(); await fetchEstates(1); }
    S.es.show = true;
  }
  renderResults(); renderDetails();
}
function setTab(tab: Tab): void {
  if (S.tab[officeSlug()] === tab) return;
  saveMem();
  S.tab[officeSlug()] = tab;
  renderTabs();
  void restoreTab();
}
function renderOfficeSel(): void {
  const sel = $<HTMLSelectElement>('#officeSel'), mine = me().offices.length ? me().offices : [officeSlug()];
  sel.innerHTML = mine.map(slugv => { const o = S.site!.offices.find(x => x.slug === slugv); return '<option value="' + esc(slugv) + '">' + esc(o?.name ?? slugv) + '</option>'; }).join('');
  sel.value = officeSlug();
  sel.disabled = mine.length < 2;
}
async function setOffice(slugv: string): Promise<void> {
  if (slugv === officeSlug()) return;
  saveMem();
  S.cards[officeSlug()] = { idx: $('#idxResult').innerHTML, bp: $('#bpResult').innerHTML };
  try {
    const m = await post<Me>('/api/v1/auth/office', { office: slugv });
    await enterOffice(m);
    toast('Now in ' + office().name + '.');
  } catch (e) { toast(errMsg(e), 'err'); renderOfficeSel(); }
}

/* ------------------------------------------------------------ account */
function renderAccount(): void {
  $('#acctName').textContent = displayName();
  $('#acctWho').textContent = displayName();
  $('#acctRole').textContent = isGuest() ? 'Guest terminal at ' + office().name : role() + ' ' + DOT + ' ' + office().name;
  $('#pwBtn').hidden = isGuest();
  $('#signOutBtn').textContent = isGuest() ? 'Leave the guest terminal' : 'Sign out';
}
export async function signOut(): Promise<void> {
  try { await post('/api/v1/auth/logout'); } catch { /* signed out anyway */ }
  setCsrf(null);
  location.assign('/');
}

/** Everything that depends on the office and the account, after sign-in or an office switch. */
async function enterOffice(m: Me): Promise<void> {
  S.me = m;
  setCsrf(m.csrf);
  S.office = await get<OfficeInfo>('/api/v1/office');
  volumes.clear();
  const o = office();
  S.forms[o.slug] ??= defaultForm(o);
  S.tab[o.slug] ??= modules()[0] ?? 'bookpage';
  if (!modules().includes(S.tab[o.slug]!)) S.tab[o.slug] = modules()[0]!;
  S.gray = can('grayscale'); S.viewAsPublic = false;
  if (!can('originals') && S.tool === 'redact') S.tool = 'pan';
  S.es = { ...S.es, q: null, rows: [], total: 0, restrictedHidden: null, page: 1, show: false, ran: false, selected: null };
  $('#sampleNote').hidden = !S.site?.tenant.sample;
  $<HTMLButtonElement>('#usersBtn').hidden = !canAdmin();
  const c = S.cards[o.slug];
  $('#idxResult').innerHTML = c?.idx ?? ''; $('#bpResult').innerHTML = c?.bp ?? '';
  renderOfficeSel(); renderAccount(); renderEstateFilters(); renderTabs(); applyForms(); renderPrinters(); updateStaffUI();
  applyFilters(S.gray, S.invert);
  $('#grayBtn').setAttribute('aria-pressed', String(S.gray));
  S.jobs = []; renderJobs(); void loadJobs();
  S.activity = []; renderAudit(); void loadActivity();
  if (isAdminOpen()) closeAdmin(true);
  refreshHelp();
  await restoreTab();
}

/* ------------------------------------------------------------ wiring */
function wire(): void {
  $('#officeSel').addEventListener('change', e => void setOffice((e.target as HTMLSelectElement).value));
  const menu = $('#acctMenu'), acct = $('#acctBtn');
  acct.addEventListener('click', () => { menu.hidden = !menu.hidden; acct.setAttribute('aria-expanded', String(!menu.hidden)); });
  document.addEventListener('click', e => { if (!menu.hidden && !(e.target as HTMLElement).closest('.acct')) { menu.hidden = true; acct.setAttribute('aria-expanded', 'false'); } });
  $('#signOutBtn').addEventListener('click', () => void signOut());
  $('#usersBtn').addEventListener('click', () => { if (isAdminOpen()) closeAdmin(); else openAdmin(); });
  $('#importBtn').addEventListener('click', () => openImport());
  $('#tabs').addEventListener('click', e => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]'); if (b) setTab(b.dataset.tab as Tab); });
  $('#tabs').addEventListener('keydown', e => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const tabs = modules();
    let i = tabs.indexOf(S.tab[officeSlug()]!);
    i = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    setTab(tabs[i]!); $('#tab-' + tabs[i]).focus(); e.preventDefault();
  });
  $('#idxWhich').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-which]');
    if (!b) return;
    saveForms();
    const f = form();
    if (f.which !== b.dataset.which) { f.which = b.dataset.which!; const ix = indexById(f.which); f.series = ix?.volumes[0]?.series ?? ix?.series[0]?.id ?? ''; }
    applyForms();
    if ($<HTMLInputElement>('#idxLast').value.trim()) void runIndexSearch(); else void openIndexSection('guide');
  });
  $('#idxForm').addEventListener('submit', e => { e.preventDefault(); void runIndexSearch(); });
  $('#idxCorp').addEventListener('change', e => { const on = (e.target as HTMLInputElement).checked; $<HTMLInputElement>('#idxInit').disabled = on; if (on) $<HTMLInputElement>('#idxInit').value = ''; });
  $('#idxChips').addEventListener('click', e => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sec]'); if (b) void openIndexSection(b.dataset.sec!); });
  $('#idxTry').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-try]');
    if (!b) return;
    const [which, last, init] = b.dataset.try!.split('|') as [string, string, string];
    const ix = indexById(which);
    Object.assign(form(), { which, series: ix?.volumes[0]?.series ?? ix?.series[0]?.id ?? '', last, init, corp: false });
    applyForms(); void runIndexSearch();
  });
  $('#bpForm').addEventListener('submit', e => { e.preventDefault(); void runBookPage($<HTMLSelectElement>('#bpType').value, $<HTMLInputElement>('#bpBook').value, $<HTMLInputElement>('#bpPage').value); });
  $('#bpType').addEventListener('change', () => { $<HTMLInputElement>('#bpBook').value = ''; $<HTMLInputElement>('#bpPage').value = '1'; });
  $('#bpTry').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-bp]');
    if (!b) return;
    const [t, bk, pg] = b.dataset.bp!.split('|') as [string, string, string];
    $<HTMLSelectElement>('#bpType').value = t; $<HTMLInputElement>('#bpBook').value = bk; $<HTMLInputElement>('#bpPage').value = pg;
    void runBookPage(t, bk, pg);
  });
  $('#docExtra').addEventListener('click', e => {
    const t = e.target as HTMLElement;
    const b = t.closest<HTMLElement>('[data-open-bp]');
    if (b) { openBookPageFromShortcut(b.dataset.openBp!); return; }
    const r = t.closest<HTMLElement>('[data-open-record]');
    if (r) { void selectRecord(r.dataset.openRecord!); return; }
    if (t.id === 'rfUndo') { renderDetails(); toast('Edits undone.'); return; }
    const act = t.closest<HTMLButtonElement>('#deleteRecord, #restoreRecord, #removeDoc');
    if (!act) return;
    if (act.id === 'restoreRecord') { void deleteRecord(true); return; }
    if (!confirmClick(act)) return;
    if (act.id === 'deleteRecord') void deleteRecord(false); else void removeDocument();
  });
  $('#docExtra').addEventListener('submit', e => { if ((e.target as HTMLElement).id === 'recForm') { e.preventDefault(); void saveRecord(); } });

  $('#idxBooks').addEventListener('change', e => { const [series, book] = (e.target as HTMLSelectElement).value.split('|'); void openIndexSection('guide', { series: series!, book: book! }); });
  $('#esForm').addEventListener('submit', e => { e.preventDefault(); void runEstateSearch(); });
  $('#esClear').addEventListener('click', () => { clearEstateForm(); void runEstateSearch(); });
  $('#esSummary').addEventListener('click', e => { if ((e.target as HTMLElement).id === 'esShow') showResults(); });
  $('#esTry').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-es]');
    if (!b) return;
    const [k, v] = b.dataset.es!.split('=') as [string, string];
    clearEstateForm();
    if (k === 'last') $<HTMLInputElement>('#esLast').value = v;
    if (k === 'sounds') { $<HTMLInputElement>('#esLast').value = v; $<HTMLInputElement>('#esSounds').checked = true; }
    if (k === 'type') $<HTMLSelectElement>('#esType').value = v;
    void runEstateSearch();
  });
  $('#rpHead').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sort]');
    if (!b) return;
    const k = b.dataset.sort!;
    S.es.sort = S.es.sort.key === k ? { key: k, dir: S.es.sort.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: k === 'docket' || k === 'dod' || k === 'issued' ? 'desc' : 'asc' };
    void goToResultsPage(1).then(() => $maybe('#rpHead [data-sort="' + k + '"]')?.focus());
  });
  $('#rpBody').addEventListener('click', e => { const tr = (e.target as HTMLElement).closest<HTMLElement>('tr[data-id]'); if (tr) void selectRecord(tr.dataset.id!, false); });
  $('#rpBody').addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const tr = (e.target as HTMLElement).closest<HTMLElement>('tr[data-id]');
    if (tr) { e.preventDefault(); void selectRecord(tr.dataset.id!, true); }
  });
  $('#rpPager').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-pg]');
    if (!b) return;
    const a = b.dataset.pg!, pages = Math.max(1, Math.ceil(S.es.total / S.es.size));
    void goToResultsPage(a === 'first' ? 1 : a === 'prev' ? S.es.page - 1 : a === 'next' ? S.es.page + 1 : pages).then(() => {
      const again = $maybe<HTMLButtonElement>('#rpPager [data-pg="' + a + '"]');
      if (again && !again.disabled) again.focus();
    });
  });
  $('#rpSize').addEventListener('change', e => { S.es.size = Number((e.target as HTMLSelectElement).value) || 25; void goToResultsPage(1); });
  $('#rpExport').addEventListener('click', () => void exportCsv());
  $('#backToResults').addEventListener('click', showResults);

  $('#prevBtn').addEventListener('click', () => void openIdx(S.idx - 1));
  $('#nextBtn').addEventListener('click', () => void openIdx(S.idx + 1));
  $('#zoomIn').addEventListener('click', () => zoomBy(1.4));
  $('#zoomOut').addEventListener('click', () => zoomBy(1 / 1.4));
  $('#fitPage').addEventListener('click', () => fit('page'));
  $('#fitWidth').addEventListener('click', () => fit('width'));
  $('#rotL').addEventListener('click', () => rotateBy(-90));
  $('#rotR').addEventListener('click', () => rotateBy(90));
  $('#grayBtn').addEventListener('click', () => { S.gray = !S.gray; applyFilters(S.gray, S.invert); $('#grayBtn').setAttribute('aria-pressed', String(S.gray)); updateIiifLine(); });
  $('#invertBtn').addEventListener('click', () => { S.invert = !S.invert; applyFilters(S.gray, S.invert); $('#invertBtn').setAttribute('aria-pressed', String(S.invert)); updateBadges(); });
  $$('.tools [data-tool]').forEach(b => b.addEventListener('click', () => setTool(b.dataset.tool as typeof S.tool)));
  $('#clearHl').addEventListener('click', clearMarks);
  $('#thumbs').addEventListener('click', e => { const b = (e.target as HTMLElement).closest<HTMLElement>('.thumb'); if (b) void openIdx(Number(b.dataset.i)); });

  $('#whatSet').addEventListener('change', () => { updateFee(); updateIiifLine(); setErr($('#printErr'), ''); });
  for (const id of ['#pFrom', '#pTo']) $(id).addEventListener('input', () => { $<HTMLInputElement>('#whatPages').checked = true; updateFee(); setErr($('#printErr'), ''); });
  $('#copies').addEventListener('input', updateFee);
  $('#printedFor').addEventListener('input', () => { $('#printedFor').removeAttribute('aria-invalid'); setErr($('#printErr'), ''); });
  $('#sendPrint').addEventListener('click', () => void sendToPrinter());
  $('#dlPdf').addEventListener('click', () => void downloadPdf());
  $('#jobList').addEventListener('click', async e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-cancel-job]');
    if (!b) return;
    try { await post(`/api/v1/print/jobs/${b.dataset.cancelJob}/cancel`); toast('Job cancelled.'); } catch (err) { toast(errMsg(err), 'err'); }
    void loadJobs();
  });

  $('#applyRedact').addEventListener('click', () => void applyRedactions());
  $('#discardRedact').addEventListener('click', discardRedactions);
  $('#delBox').addEventListener('click', () => boxes?.deleteSelected());
  $('#viewPublic').addEventListener('change', e => { S.viewAsPublic = (e.target as HTMLInputElement).checked; if (S.viewAsPublic && S.tool === 'redact') S.tool = 'pan'; void openIdx(S.idx); });
  $('#saveRot').addEventListener('click', () => void saveRotation());

  document.addEventListener('keydown', e => {
    if (e.key === 'F1') { e.preventDefault(); if (isHelpOpen()) closeHelp(); else openHelp(helpContext()); return; }
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target as HTMLElement;
    if (e.key === 'Escape' && isHelpOpen() && !t.closest('dialog')) { closeHelp(); e.preventDefault(); return; }
    if (t.closest('input, select, textarea, dialog, [contenteditable="true"]')) return;
    if (e.key === '?') { openHelp(); e.preventDefault(); return; }
    if (t.closest('#helpPanel') || isAdminOpen() || $('#app').hidden) return;
    if (!$('#resultsPane').hidden) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { void goToResultsPage(S.es.page + (e.key === 'ArrowLeft' ? -1 : 1)); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowLeft') { void openIdx(S.idx - 1); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { void openIdx(S.idx + 1); e.preventDefault(); }
    else if (e.key === '+' || e.key === '=') zoomBy(1.4);
    else if (e.key === '-' || e.key === '_') zoomBy(1 / 1.4);
    else if (e.key === 'r') rotateBy(90);
    else if (e.key === 'R') rotateBy(-90);
    else if (e.key === 'Delete' || e.key === 'Backspace') { if (boxes?.deleteSelected()) e.preventDefault(); }
    else if (e.key === 'Escape') setTool('pan', true);
  });
}

let started = false;
/** Show the records screen for a signed-in account. */
export async function startApp(m: Me): Promise<void> {
  $('#app').hidden = false;
  if (!started) {
    started = true;
    initViewer({
      opened: () => void onOpened(),
      failed: msg => stageMessage(msg),
      rotated: () => { updateBadges(); updateStaffUI(); updateIiifLine(); },
      moved: throttle(updateIiifLine, 150),
      boxesChanged: afterBoxesChanged,
      selectionChanged: updateStaffUI,
    });
    wire(); wireAdmin({ afterSelfChange: async () => { await enterOffice(await get<Me>('/api/v1/auth/me')); } }); wireImport({ openBookPage: (t, b, p) => openImported(t, b, p) }); wireHelp({ allowedPrinters, canDownload });
  }
  await enterOffice(m);
}

/** After an import: show the new pages. */
function openImported(type: string, book: string, page: string): void {
  if (!hasModule('bookpage')) return;
  saveMem();
  Object.assign(form(), { bpType: type, bpBook: book, bpPage: page });
  S.mem[officeSlug()]!.bookpage = null;
  S.tab[officeSlug()] = 'bookpage';
  renderTabs(); applyForms(); void restoreTab();
}

